import { useState } from 'react';
import { toast } from 'sonner';
import { useTier } from '../../context/TierContext';
import { UpgradePrompt } from '../../components/FeatureGate';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { ConfirmationDialog } from '../../components/ui/confirmation-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Pencil, Trash2, Search, MoreHorizontal, Palette } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

// Predefined color options for worker types
const COLOR_OPTIONS = [
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Teal', value: '#14B8A6' },
];

const WorkerTypesTab = ({ api, workerTypes, fetchWorkerTypes }) => {
  const { hasFeature } = useTier();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    default_rate: '',
    color: '#8B5CF6',
    is_active: true
  });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });

  const activeCount = workerTypes.filter(t => t.is_active).length;
  const customCount = workerTypes.filter(t => !t.is_system).length;

  const filteredTypes = workerTypes.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setForm({ name: '', description: '', default_rate: '', color: '#8B5CF6', is_active: true });
    setEditingType(null);
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setForm({
        name: type.name,
        description: type.description || '',
        default_rate: type.default_rate?.toString() || '',
        color: type.color || '#8B5CF6',
        is_active: type.is_active
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...form,
        default_rate: form.default_rate ? parseFloat(form.default_rate) : null
      };
      if (editingType) {
        await api.put(`/worker-types/${editingType.id}`, submitData);
        toast.success('Worker type updated');
      } else {
        await api.post('/worker-types', submitData);
        toast.success('Worker type created');
      }
      setDialogOpen(false);
      resetForm();
      fetchWorkerTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (type) => {
    if (type.is_system) {
      toast.error('System types cannot be deleted');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Delete Worker Type',
      description: `Delete worker type "${type.name}"?`,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await api.delete(`/worker-types/${type.id}`);
          toast.success('Worker type deleted');
          fetchWorkerTypes();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Delete failed');
        }
      },
    });
  };

  const handleToggleActive = async (type) => {
    try {
      await api.put(`/worker-types/${type.id}`, {
        name: type.name,
        description: type.description,
        default_rate: type.default_rate,
        color: type.color,
        is_active: !type.is_active
      });
      toast.success(`Worker type ${type.is_active ? 'deactivated' : 'activated'}`);
      fetchWorkerTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  if (!hasFeature('worker_types')) {
    return (
      <UpgradePrompt 
        feature="worker_types"
        title="Worker Types"
        description="Define additional worker roles like Dogman, Spotter, and Rigger for your jobs. Available on Advanced plan and above."
      />
    );
  }

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Worker Types</CardTitle>
              <CardDescription>
                {activeCount} active, {customCount} custom worker types
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-worker-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Worker Type
            </Button>
          </div>
          <div className="relative max-w-sm mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="worker-search"
              placeholder="Search worker types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="label-overline">Color</TableHead>
                <TableHead className="label-overline">Name</TableHead>
                <TableHead className="label-overline">Type</TableHead>
                <TableHead className="label-overline">Default Rate</TableHead>
                <TableHead className="label-overline">Status</TableHead>
                <TableHead className="label-overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTypes.map((type) => (
                <TableRow key={type.id} data-testid={`worker-row-${type.id}`}>
                  <TableCell>
                    <div 
                      className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: type.color || '#8B5CF6' }}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{type.name}</TableCell>
                  <TableCell>
                    {type.is_system ? (
                      <Badge variant="secondary">System</Badge>
                    ) : (
                      <Badge className="bg-[#ffd108]/20 text-[#ffd108]">Custom</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono">
                    {type.default_rate ? `$${type.default_rate.toFixed(2)}/hr` : '-'}
                  </TableCell>
                  <TableCell>
                    <Switch 
                      checked={type.is_active}
                      onCheckedChange={() => handleToggleActive(type)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(type)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {!type.is_system && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDelete(type)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Worker Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingType ? 'Edit Worker Type' : 'Add Worker Type'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="worker-form">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Operator, Laborer"
                required
                disabled={editingType?.is_system}
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="default_rate">Default Hourly Rate ($)</Label>
              <Input
                id="default_rate"
                type="number"
                step="0.01"
                min="0"
                value={form.default_rate}
                onChange={(e) => setForm({ ...form, default_rate: e.target.value })}
                placeholder="0.00"
                className="font-mono"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <Palette className="h-4 w-4" />
                Display Color
              </Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setForm({ ...form, color: color.value })}
                    className={`w-full aspect-square rounded-lg border-2 transition-all hover:scale-110 ${
                      form.color === color.value 
                        ? 'border-white ring-2 ring-offset-2 ring-offset-background ring-current' 
                        : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                This color will be used for worker badges throughout the app
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {editingType ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    
      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        confirmText="Delete"
        variant="destructive"
      />
    </>
  );
};

export default WorkerTypesTab;
